import React, { useEffect, useRef, useState } from 'react';
import { Card, Dropdown, DropdownButton, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import axiosInstance from 'utils/axiosInterceptors';
import { formatDate, formatNumberWithCommas } from 'utils/formats';
import { TDiscount, TDiscountCodes } from 'utils/types';
import { PurchaseOrderHandle } from '../PurchaseOrder/CreatePurchaseOrderForm';
import CustomModal, {
  CustomModalProps,
  ModalHandle
} from 'components/modals/CustomModal';
import UpdateDiscountForm from './UpdateDiscountForm';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';
import { AxiosError } from 'axios';
import BadgeStatus from 'utils/BadgeStatus';
import { ColumnDef } from '@tanstack/react-table';
import DataTable from '../BranchProductInventory/DataTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';

const DiscountDetails = () => {
  const { id: discountId } = useParams();
  const [discountDetails, setDiscountDetails] = useState<TDiscount | null>(
    null
  );
  const [modalProps, setModalProps] = useState<CustomModalProps>();
  const modalRef = useRef<ModalHandle>({
    show: () => {},
    hide: () => {}
  });
  const editDiscountFormRef = useRef<PurchaseOrderHandle>(null);

  const fetchDiscountCodes = async (
    page: number,
    pageSize: number,
    search: string,
    orderBy: string,
    orderByDesc: boolean
  ) => {
    const { data } = await axiosInstance.get(
      `/api/v1/discounts/${discountId}/discountCodes`,
      {
        params: {
          page,
          pageSize,
          search,
          orderBy,
          orderByDesc
        }
      }
    );
    return data;
  };

  const getDiscountDetails = async () => {
    try {
      const res = await axiosInstance.get(`/api/v1/discounts/${discountId}`);
      setDiscountDetails(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const updateDiscount = () => {
    setModalProps({
      title: 'Update Discount',
      content: (
        <UpdateDiscountForm
          ref={editDiscountFormRef}
          discountId={parseInt(discountId as string)}
          discountName={discountDetails?.discountName as string}
          description={discountDetails?.description as string}
          discountAmount={discountDetails?.discountAmount as number}
          discountPercentage={discountDetails?.discountPercentage as number}
          minimumProductAmount={discountDetails?.minimumProductAmount as number}
          minimumInvoiceAmount={discountDetails?.minimumInvoiceAmount as number}
          maximumDiscountAmount={
            discountDetails?.maximumDiscountAmount as number
          }
          usageLimit={discountDetails?.usageLimit as number}
          startDate={discountDetails?.startDate as Date}
          endDate={discountDetails?.endDate as Date}
          branchId={discountDetails?.branchId ?? null}
          isAfterSalesDiscount={
            discountDetails?.isAfterSalesDiscount as boolean
          }
          isForAllCustomers={discountDetails?.isForAllCustomers as boolean}
          numberOfDaysBeforeActive={discountDetails?.numberOfDaysBeforeActive as number}
          isForMembers={discountDetails?.isForMembers as boolean}
        />
      ),
      onSubmit: () => handleSubmitUpdatedDiscount(),
      onCancel: () => modalRef.current.hide()
    });
    modalRef.current.show();
  };

  const handleSubmitUpdatedDiscount = async () => {
    if (editDiscountFormRef.current) {
      try {
        const isSuccess = await editDiscountFormRef.current.submit();
        if (isSuccess) {
          modalRef.current.hide();
          getDiscountDetails();
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleActivateDiscount = async () => {
    try {
      await axiosInstance.put(`/api/v1/discounts/${discountId}/activate`);
      toast.success('Discount Activated', { ...toastConfig, autoClose: 2000 });
      getDiscountDetails();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const handleDeactivateDiscount = async () => {
    try {
      await axiosInstance.put(`/api/v1/discounts/${discountId}/deactivate`);
      toast.success('Discount Deactivated', {
        ...toastConfig,
        autoClose: 2000
      });
      getDiscountDetails();
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const columns: ColumnDef<TDiscountCodes>[] = [
    {
      id: 'code',
      header: 'code',
      accessorKey: 'code',
      enableSorting: false
    },
    {
      id: 'isUsed',
      header: 'Is Used',
      accessorKey: 'isUsed',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const isUsed = original.isUsed;
        return (
          <FontAwesomeIcon
            icon={isUsed ? faCheckCircle : faTimesCircle}
            style={{ color: isUsed ? 'green' : 'red' }}
          />
        );
      }
    },
    {
      id: 'activeDate',
      header: 'Active Date',
      accessorKey: 'activeDate',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        return <span>{formatDate(original.activeDate)}</span>;
      }
    }
  ];

  useEffect(() => {
    getDiscountDetails();
  }, [discountId]);

  return (
    <>
      <Row lg="auto" className="d-flex justify-content-between mb-3">
        <h4>Discount Details</h4>
        <DropdownButton title="Actions" variant="phoenix-primary">
          <Dropdown.Item onClick={updateDiscount}>Update Details</Dropdown.Item>
          {discountDetails?.status === 'Active' ? (
            <Dropdown.Item onClick={handleDeactivateDiscount}>
              Dectivate
            </Dropdown.Item>
          ) : (
            <Dropdown.Item onClick={handleActivateDiscount}>
              Activate
            </Dropdown.Item>
          )}
        </DropdownButton>
      </Row>
      <Card className="mb-3">
        <Card.Body className="d-flex justify-content-between">
          <div className="w-50">
            <Card.Title as="h6">
              Discount Name: {discountDetails?.discountName}
            </Card.Title>
            {discountDetails?.discountAmount !== null ? (
              <Card.Title as="h6">
                Discount Amount: {discountDetails?.discountAmount}
              </Card.Title>
            ) : (
              <Card.Title as="h6">
                Discount Percentage: {discountDetails?.discountPercentage}%
              </Card.Title>
            )}
            <Card.Title as="h6">
              Minimum Product Amount:{' '}
              {formatNumberWithCommas(discountDetails?.minimumProductAmount)}
            </Card.Title>
            <Card.Title as="h6">
              Minimum Invoice Amount:{' '}
              {formatNumberWithCommas(discountDetails?.minimumInvoiceAmount)}
            </Card.Title>
            <Card.Title as="h6">
              Maximum Discount Amount:{' '}
              {formatNumberWithCommas(discountDetails?.maximumDiscountAmount)}
            </Card.Title>
            <Card.Title as="h6">
              Usage Limit: {discountDetails?.usageLimit}
            </Card.Title>
            <Card.Title as="h6">
              Number of Days Before Active: {discountDetails?.numberOfDaysBeforeActive}
            </Card.Title>
          </div>
          <div className="w-50">
            <Card.Title as="h6">
              Start Date: {formatDate(discountDetails?.startDate as string)}
            </Card.Title>
            <Card.Title as="h6">
              End Date: {formatDate(discountDetails?.endDate as string)}
            </Card.Title>
            <Card.Title as="h6">
              Status: <BadgeStatus status={discountDetails?.status} />
            </Card.Title>
            <Card.Title as="h6">
              Branch: {discountDetails?.branchName}
            </Card.Title>
            <Card.Title as="h6">
              All Customers:{' '}
              <FontAwesomeIcon
                icon={
                  discountDetails?.isForAllCustomers
                    ? faCheckCircle
                    : faTimesCircle
                }
                style={{
                  color: discountDetails?.isForAllCustomers ? 'green' : 'red'
                }}
              />
            </Card.Title>
            <Card.Title as="h6">
              After Sales:{' '}
              <FontAwesomeIcon
                icon={
                  discountDetails?.isAfterSalesDiscount
                    ? faCheckCircle
                    : faTimesCircle
                }
                style={{
                  color: discountDetails?.isAfterSalesDiscount ? 'green' : 'red'
                }}
              />
            </Card.Title>
            <Card.Title as="h6">
              For Members:{' '}
              <FontAwesomeIcon
                icon={
                  discountDetails?.isForMembers
                    ? faCheckCircle
                    : faTimesCircle
                }
                style={{
                  color: discountDetails?.isForMembers ? 'green' : 'red'
                }}
              />
            </Card.Title>
          </div>
        </Card.Body>
      </Card>

      <DataTable
        queryKey={['discountCodes']}
        queryFn={fetchDiscountCodes}
        columns={columns}
        showActions={false}
      />

      <CustomModal
        ref={modalRef}
        title={modalProps?.title}
        content={modalProps?.content}
        onCancel={modalProps?.onCancel}
        onSubmit={modalProps?.onSubmit}
        modalSize="lg"
      />
    </>
  );
};

export default DiscountDetails;
